import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { ContentCopyOutlined, Search } from "@mui/icons-material";
import type { GridApiPremium, GridColDef } from "@mui/x-data-grid-premium";
import { isUserFacingColumn } from "../jumpToColumnOptions";
import {
  AdvancedCopyOptions,
  COPY_FORMATS,
  CopyFormat,
  sanitizeAdvancedCopyOptions,
  serializeAdvancedCopy,
} from "../advancedCopy";

interface AdvancedCopyDialogProps {
  rows: any[];
  apiRef: React.RefObject<GridApiPremium | null>;
  tableName: string;
  onClose: () => void;
}

interface CopyableColumn {
  field: string;
  label: string;
  hidden: boolean;
  colDef: GridColDef;
}

interface StoredSettings {
  options?: unknown;
  lastFields?: unknown;
}

const PREVIEW_LINES = 12;
const storageKey = (tableName: string) => `sdg-advanced-copy:${tableName}`;

function loadSettings(tableName: string): StoredSettings {
  try {
    const raw = localStorage.getItem(storageKey(tableName));
    return raw ? (JSON.parse(raw) as StoredSettings) : {};
  } catch {
    return {};
  }
}

function saveSettings(tableName: string, settings: StoredSettings) {
  try {
    localStorage.setItem(storageKey(tableName), JSON.stringify(settings));
  } catch {
    // Storage unavailable: settings just aren't remembered
  }
}

/**
 * DBeaver-style Advanced Copy: pick columns and a format, preview the result
 * and copy the selected rows to the clipboard. Mounted only while open, so
 * each open starts from the grid's current visible columns and the last-used
 * options for this table.
 */
export function AdvancedCopyDialog({
  rows,
  apiRef,
  tableName,
  onClose,
}: AdvancedCopyDialogProps) {
  const [stored] = useState(() => loadSettings(tableName));

  // Grid order, including columns the user has hidden
  const [columns] = useState<CopyableColumn[]>(() => {
    const api = apiRef.current;
    if (!api) return [];
    const visible = new Set(api.getVisibleColumns().map((col) => col.field));
    return api
      .getAllColumns()
      .filter((col) => isUserFacingColumn(col))
      .map((col) => ({
        field: col.field,
        label: col.headerName?.trim() || col.field,
        hidden: !visible.has(col.field),
        colDef: col,
      }));
  });

  const visibleFields = useMemo(
    () => columns.filter((c) => !c.hidden).map((c) => c.field),
    [columns]
  );
  const lastFields = useMemo(() => {
    const saved = Array.isArray(stored.lastFields) ? stored.lastFields : [];
    const known = new Set(columns.map((c) => c.field));
    return saved.filter(
      (field): field is string => typeof field === "string" && known.has(field)
    );
  }, [stored, columns]);

  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(visibleFields)
  );
  const [options, setOptions] = useState<AdvancedCopyOptions>(() =>
    sanitizeAdvancedCopyOptions(stored.options)
  );
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"idle" | "copied" | "failed">("idle");
  const fallbackRef = useRef<HTMLTextAreaElement>(null);

  const setOption = <K extends keyof AdvancedCopyOptions>(
    key: K,
    value: AdvancedCopyOptions[K]
  ) => {
    setOptions((prev) => ({ ...prev, [key]: value }));
    setStatus("idle");
  };

  const toggleField = (field: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field);
      else next.add(field);
      return next;
    });
    setStatus("idle");
  };

  const selectOnly = (fields: string[]) => {
    setSelected(new Set(fields));
    setStatus("idle");
  };

  const query = search.trim().toLowerCase();
  const listedColumns = query
    ? columns.filter(
        (c) =>
          c.label.toLowerCase().includes(query) ||
          c.field.toLowerCase().includes(query)
      )
    : columns;

  const copyColumns = useMemo(
    () => columns.filter((c) => selected.has(c.field)),
    [columns, selected]
  );

  const text = useMemo(() => {
    const api = apiRef.current;
    if (!api || copyColumns.length === 0) return "";
    const read = (row: any, colDef: GridColDef) => {
      try {
        return options.valueSource === "raw"
          ? api.getRowValue(row, colDef)
          : api.getRowFormattedValue(row, colDef);
      } catch {
        return null;
      }
    };
    const matrix = rows.map((row) =>
      copyColumns.map((c) => read(row, c.colDef))
    );
    return serializeAdvancedCopy(
      copyColumns.map(({ field, label }) => ({ field, label })),
      matrix,
      options
    );
  }, [apiRef, rows, copyColumns, options]);

  const previewLines = text.split(/\r?\n/);
  const preview =
    previewLines.length > PREVIEW_LINES
      ? `${previewLines.slice(0, PREVIEW_LINES).join("\n")}\n…`
      : previewLines.join("\n");

  const handleCopy = async () => {
    saveSettings(tableName, {
      options,
      lastFields: copyColumns.map((c) => c.field),
    });
    try {
      await navigator.clipboard.writeText(text);
      setStatus("copied");
      setTimeout(onClose, 700);
    } catch {
      setStatus("failed");
      setTimeout(() => fallbackRef.current?.select(), 0);
    }
  };

  const { format } = options;
  const isDelimited = format === "tsv" || format === "csv";
  const rowLabel = `${rows.length} row${rows.length === 1 ? "" : "s"}`;

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Advanced Copy
        <Typography variant="body2" color="text.secondary">
          {rowLabel} selected
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 3,
          }}
        >
          {/* Columns */}
          <Box sx={{ width: { md: 280 }, flexShrink: 0, minWidth: 0 }}>
            <Typography variant="subtitle2" gutterBottom>
              Columns ({copyColumns.length} of {columns.length})
            </Typography>
            <TextField
              id="advanced-copy-column-search"
              size="small"
              fullWidth
              placeholder="Find a column"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, my: 1 }}>
              <Button size="small" onClick={() => selectOnly(visibleFields)}>
                Visible
              </Button>
              <Button
                size="small"
                onClick={() => selectOnly(columns.map((c) => c.field))}
              >
                All
              </Button>
              <Button size="small" onClick={() => selectOnly([])}>
                None
              </Button>
              <Button
                size="small"
                onClick={() => selectOnly(lastFields)}
                disabled={lastFields.length === 0}
              >
                Last used
              </Button>
            </Box>
            <Box
              sx={{
                maxHeight: 340,
                overflowY: "auto",
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                px: 1,
              }}
            >
              {listedColumns.length === 0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 1 }}
                >
                  No columns match "{search}"
                </Typography>
              )}
              {listedColumns.map((col) => (
                <FormControlLabel
                  key={col.field}
                  sx={{ display: "flex", mr: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={selected.has(col.field)}
                      onChange={() => toggleField(col.field)}
                    />
                  }
                  label={
                    <Typography variant="body2" component="span">
                      {col.label}
                      {col.hidden && (
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                        >
                          {" "}
                          (hidden)
                        </Typography>
                      )}
                    </Typography>
                  }
                />
              ))}
            </Box>
          </Box>

          {/* Format and options */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <TextField
              id="advanced-copy-format"
              select
              size="small"
              label="Format"
              value={format}
              onChange={(e) =>
                setOption("format", e.target.value as CopyFormat)
              }
              helperText={COPY_FORMATS.find((f) => f.value === format)?.hint}
            >
              {COPY_FORMATS.map((f) => (
                <MenuItem key={f.value} value={f.value}>
                  {f.label}
                </MenuItem>
              ))}
            </TextField>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              {format !== "list" && (
                <TextField
                  id="advanced-copy-header-source"
                  select
                  size="small"
                  label={format === "json" ? "Keys" : "Header text"}
                  value={options.headerSource}
                  onChange={(e) =>
                    setOption(
                      "headerSource",
                      e.target.value as AdvancedCopyOptions["headerSource"]
                    )
                  }
                  sx={{ minWidth: 170 }}
                >
                  <MenuItem value="label">Column names</MenuItem>
                  <MenuItem value="field">Field ids</MenuItem>
                </TextField>
              )}
              <TextField
                id="advanced-copy-value-source"
                select
                size="small"
                label="Values"
                value={options.valueSource}
                onChange={(e) =>
                  setOption(
                    "valueSource",
                    e.target.value as AdvancedCopyOptions["valueSource"]
                  )
                }
                sx={{ minWidth: 170 }}
              >
                <MenuItem value="formatted">As shown in the grid</MenuItem>
                <MenuItem value="raw">Raw values</MenuItem>
              </TextField>
              {format !== "json" && (
                <TextField
                  id="advanced-copy-null-text"
                  size="small"
                  label="Empty cells as"
                  placeholder="(leave blank)"
                  value={options.nullText}
                  onChange={(e) => setOption("nullText", e.target.value)}
                  sx={{ width: 150 }}
                />
              )}
            </Box>

            {isDelimited && (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  alignItems: "center",
                }}
              >
                <TextField
                  id="advanced-copy-quote-mode"
                  select
                  size="small"
                  label="Quote values"
                  value={options.quoteMode}
                  onChange={(e) =>
                    setOption(
                      "quoteMode",
                      e.target.value as AdvancedCopyOptions["quoteMode"]
                    )
                  }
                  sx={{ minWidth: 170 }}
                >
                  <MenuItem value="auto">Only when needed</MenuItem>
                  <MenuItem value="always">Always</MenuItem>
                  <MenuItem value="never">Never</MenuItem>
                </TextField>
                <FormControlLabel
                  control={
                    <Checkbox
                      id="advanced-copy-include-header"
                      size="small"
                      checked={options.includeHeader}
                      onChange={(e) =>
                        setOption("includeHeader", e.target.checked)
                      }
                    />
                  }
                  label={<Typography variant="body2">Header row</Typography>}
                />
              </Box>
            )}

            {(isDelimited || format === "markdown") && (
              <FormControlLabel
                sx={{ mt: -1 }}
                control={
                  <Checkbox
                    id="advanced-copy-row-numbers"
                    size="small"
                    checked={options.includeRowNumbers}
                    onChange={(e) =>
                      setOption("includeRowNumbers", e.target.checked)
                    }
                  />
                }
                label={<Typography variant="body2">Row numbers</Typography>}
              />
            )}

            {format === "list" && (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  alignItems: "center",
                }}
              >
                <TextField
                  id="advanced-copy-list-separator"
                  size="small"
                  label="Separator"
                  value={options.listSeparator}
                  onChange={(e) => setOption("listSeparator", e.target.value)}
                  sx={{ width: 110 }}
                />
                <TextField
                  id="advanced-copy-list-quote"
                  select
                  size="small"
                  label="Quotes"
                  value={options.listQuote}
                  onChange={(e) =>
                    setOption(
                      "listQuote",
                      e.target.value as AdvancedCopyOptions["listQuote"]
                    )
                  }
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="none">None</MenuItem>
                  <MenuItem value="single">'Single'</MenuItem>
                  <MenuItem value="double">"Double"</MenuItem>
                </TextField>
                <FormControlLabel
                  control={
                    <Checkbox
                      id="advanced-copy-distinct"
                      size="small"
                      checked={options.distinct}
                      onChange={(e) => setOption("distinct", e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="body2">Remove duplicates</Typography>
                  }
                />
              </Box>
            )}

            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" gutterBottom>
                Preview
              </Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1.5,
                  minHeight: 80,
                  maxHeight: 240,
                  overflow: "auto",
                  bgcolor: "action.hover",
                  borderRadius: 1,
                  fontFamily: "monospace",
                  fontSize: 12,
                  lineHeight: 1.5,
                  whiteSpace: "pre",
                  tabSize: 12,
                }}
              >
                {text ? preview : "Select at least one column."}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {rowLabel} × {copyColumns.length} column
                {copyColumns.length === 1 ? "" : "s"} ·{" "}
                {text.length.toLocaleString()} characters
              </Typography>
            </Box>

            {status === "failed" && (
              <Box>
                <Alert severity="warning" sx={{ mb: 1 }}>
                  The browser blocked clipboard access. The full text is
                  selected below; press Ctrl+C to copy it.
                </Alert>
                <TextField
                  id="advanced-copy-fallback"
                  multiline
                  fullWidth
                  minRows={3}
                  maxRows={8}
                  value={text}
                  inputRef={fallbackRef}
                  slotProps={{
                    htmlInput: { readOnly: true, spellCheck: false },
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mr: "auto" }}
        >
          Alt+C copies the selected rows as tab-separated text with headers.
        </Typography>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={<ContentCopyOutlined />}
          onClick={handleCopy}
          disabled={!text || rows.length === 0}
          color={status === "copied" ? "success" : "primary"}
        >
          {status === "copied" ? "Copied" : `Copy ${rowLabel}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
